import type { ProfileLookup } from "Api/Queries/Social/usePublicProfileQuery";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import Error404Page from "Pages/Error/Error404Page";
import { AuthenticatedPublicProfilePage } from "Pages/PublicProfile/AuthenticatedPublicProfilePage";
import PublicProfilePage from "Pages/PublicProfile/PublicProfilePage";
import { isValidHandle, normalizeHandle } from "Schemas/handleSchema";
import { useParams } from "react-router";
import { isGuid } from "Utils/StringUtils";

/**
 * /profile/{key} switch (ZS-1320): the key is a handle or a persona PublicID — the only
 * address of handle-less users (the handle is just one way to find someone). The URL
 * serves BOTH audiences — signed-in viewers get the profile inside the standard app
 * layout, anonymous visitors the bare page — so unlike AuthenticatedRoute/AnonymousRoute
 * nobody is redirected. Keys that are neither GUID- nor handle-shaped render the 404;
 * unknown-but-valid keys get the pages' explicit "doesn't exist" state.
 */
export const PublicProfileRoute = () => {
  const user = useLoggedInUser();
  const { profileKey } = useParams();

  let lookup: ProfileLookup | null = null;

  if (profileKey && isGuid(profileKey)) {
    lookup = { identityUserPublicID: profileKey.toLowerCase() };
  } else if (profileKey) {
    const handle = normalizeHandle(profileKey);
    lookup = isValidHandle(handle) ? { handle } : null;
  }

  if (!lookup) {
    return <Error404Page />;
  }

  return user ? (
    <AuthenticatedPublicProfilePage lookup={lookup} />
  ) : (
    <PublicProfilePage lookup={lookup} />
  );
};
